// PayrollTable.jsx
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { startCase } from "lodash";
import { AiFillEye, AiFillEdit, AiFillDelete } from "react-icons/ai";
import { FaCheckCircle, FaClock } from "react-icons/fa";
import { FiDownload } from "react-icons/fi";
import helpers from "../../utils/helpers";
import { apiGet } from "../../utils/apiFetch";
import apiPath from "../../utils/apiPath";
import useToastContext from "hooks/useToastContext";

const PayrollTable = ({ payrollList, handleView, handleEdit, handleDelete, page }) => {
  const { t } = useTranslation();
  const notification = useToastContext();
  const [downloadingId, setDownloadingId] = useState(null);

  const handleDownloadStub = async (item) => {
    try {
      setDownloadingId(item._id);
      const res = await apiGet(`${apiPath.payrollBase}/${item._id}/pay-stub`);
      if (res?.data?.success && res.data.results?.url) {
        window.open(res.data.results.url, "_blank");
      } else {
        notification.error(res?.data?.message || "Pay stub not available yet");
      }
    } catch (err) {
      notification.error("Something went wrong");
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div>
      <div className="p-3">
        <div className="border overflow-x-auto relative rounded-lg">
          <table className="text-left w-full text-xs text-[#A5A5A5] dark:text-gray-400">
            <thead className="text-gray-900 text-xs border border-[#E1E6EE] bg-[#E1E6EE] dark:bg-gray-700 dark:text-gray-400">
              <tr>
                <th scope="col" className="px-5 py-3">Sr No.</th>
                <th className="py-3 px-5" scope="col">Employee</th>
                <th scope="col" className="py-3 px-5">Pay Period</th>
                <th scope="col" className="py-3 px-5">Regular Hours</th>
                <th scope="col" className="py-3 px-5">Overtime Hours</th>
                <th scope="col" className="py-3 px-5">Gross Pay</th>
                <th scope="col" className="py-3 px-5">Net Pay</th>
                <th scope="col" className="text-center py-3 px-5">Status</th>
                <th scope="col" className="text-center py-3 px-5">Everee</th>
                <th className="py-3 px-5 text-center" scope="col">Action</th>
              </tr>
            </thead>
            <tbody>
              {payrollList?.map((item, index) => (
                <tr key={item._id} className="bg-white border-b dark:bg-gray-800 dark:border-gray-700">
                  <td className="py-5 px-5 border-r text-gray-900">
                    {(page - 1) * 10 + index + 1}
                  </td>
                  <td className="py-5 px-5 border-r text-gray-900">
                    {item?.employee?.name}<br/>
                    <span className="text-[10px] text-gray-500">{item?.employee?.employeeId}</span>
                  </td>
                  <td className="py-5 px-5 border-r text-gray-900">
                    {item?.payPeriod?.startDate && item?.payPeriod?.endDate ?
                      `${dayjs(item.payPeriod.startDate).format("MM-DD-YYYY")} to ${dayjs(item.payPeriod.endDate).format("MM-DD-YYYY")}` :
                      'N/A'
                    }
                  </td>
                  <td className="py-5 px-5 border-r text-gray-900">{Number(item?.regularHours || 0).toFixed(2)}h</td>
                  <td className="py-5 px-5 border-r text-gray-900">{Number(item?.overtimeHours || 0).toFixed(2)}h</td>
                  <td className="py-5 px-5 border-r text-gray-900">
                    {item?.grossPay === null || item?.grossPay === undefined
                      ? <span className="text-gray-400 italic">pending Everee</span>
                      : `$${Number(item.grossPay).toFixed(2)}`}
                  </td>
                  <td className="py-5 px-5 border-r text-gray-900">
                    {item?.netPay === null || item?.netPay === undefined
                      ? <span className="text-gray-400 italic">pending Everee</span>
                      : `$${Number(item.netPay).toFixed(2)}`}
                  </td>
                  <td className="py-5 px-5 border-r text-gray-900 text-center">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      item?.status === 'completed' ? 'bg-green-100 text-green-700' :
                      item?.status === 'processing' ? 'bg-blue-100 text-blue-700' :
                      item?.status === 'failed' ? 'bg-red-100 text-red-700' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>
                      {startCase(item?.status)}
                    </span>
                  </td>
                  <td className="py-5 px-5 border-r text-gray-900 text-center">
                    {item?.everee?.synced ? (
                      <span className="inline-flex items-center gap-1 text-green-700 text-[10px]" title={`Payment ID: ${item.everee.paymentId}`}>
                        <FaCheckCircle className="w-3 h-3" /> Synced
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-yellow-700 text-[10px]">
                        <FaClock className="w-3 h-3" /> Awaiting
                      </span>
                    )}
                  </td>
                  <td className="py-5 px-5 border-l text-gray-900 text-center">
                    <ul className="flex justify-center">
                      <li onClick={() => handleView(item)} className="px-2 py-2 hover:bg-white hover:text-LightBlue">
                        <a title="View">
                          <AiFillEye className="cursor-pointer w-5 h-5 text-slate-600" />
                        </a>
                      </li>
                      <li onClick={() => handleEdit(item)} className="px-2 py-2 hover:bg-white hover:text-LightBlue">
                        <a title="Edit">
                          <AiFillEdit className="cursor-pointer w-5 h-5 text-slate-600" />
                        </a>
                      </li>
                      {item?.everee?.synced && (
                        <li onClick={() => !downloadingId && handleDownloadStub(item)} className="px-2 py-2 hover:bg-white hover:text-LightBlue">
                          <a title="Download Pay Stub">
                            <FiDownload className={`cursor-pointer w-5 h-5 text-slate-600 ${downloadingId === item._id ? 'animate-pulse' : ''}`} />
                          </a>
                        </li>
                      )}
                      <li onClick={() => {
                        helpers.alertFunction(
                          item?.everee?.synced
                            ? `This payroll reflects a real Everee payment. Delete anyway? (Only removes it from Empivo — does not undo the payment.)`
                            : `Are you sure want to delete this payroll?`,
                          item._id,
                          handleDelete,
                          true
                        );
                      }} className="px-2 py-2 hover:bg-white hover:text-slate-800">
                        <a title="Delete">
                          <AiFillDelete className="cursor-pointer w-5 h-5 text-slate-600" />
                        </a>
                      </li>
                    </ul>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PayrollTable;